/**
 * Pieces shared by the Upgrades dock (Y) and the City panel's Upgrades tab:
 * `useTickMemo` (a list that only needs recomputing every few ticks while
 * nothing shows it), the one-line `UpgradeRow` and the `UpgradeFold` that
 * opens a row into the shared `UpgradeCard` with its action. The list comes
 * from `view/upgradesList.ts`; a row's button sends the entry's own commands
 * (the ones the Units and City panels send).
 */
import { useRef } from 'react';
import type { Command } from '@/game/sim';
import type { UpgradeEntry } from '@/game/view/upgradesList';
import { VERBS } from '@/game/view/verbs';
import { Cost } from './Cost';
import { UpgradeActionRow, UpgradeCard } from './UpgradeCard';

/**
 * `compute()`'s result, reused until `every` ticks passed or `key` changed
 * (list building is not free, and a closed pill only needs a rough count).
 */
export function useTickMemo<T>(compute: () => T, tick: number, every: number, key: string): T {
  const memo = useRef<{ tick: number; key: string; value: T } | null>(null);
  const hit = memo.current;
  if (hit && hit.key === key && Math.abs(tick - hit.tick) < every) return hit.value;
  const value = compute();
  memo.current = { tick, key, value };
  return value;
}

export function sendEntry(entry: UpgradeEntry, dispatch: (c: Command) => void) {
  for (const command of entry.commands) dispatch(command);
}

const glyphOf = (entry: UpgradeEntry) => VERBS[entry.preview.verb].glyph;

/** "×5 of 9": how many can go now of how many qualify (not for a lone level). */
function countText(entry: UpgradeEntry): string | null {
  return entry.of > 1 || entry.kind === 'unit' || entry.kind === 'rank'
    ? `×${entry.ready} of ${entry.of}`
    : null;
}

/** One list row: glyph, title, one-line effect, count and cost. */
export function UpgradeRow({
  entry,
  selected,
  expanded,
  stock,
  onSelect,
  onHighlight,
}: {
  entry: UpgradeEntry;
  selected: boolean;
  /** Set when the row folds a card open (City panel). */
  expanded?: boolean;
  stock: Readonly<Record<string, number>>;
  onSelect: () => void;
  onHighlight?: (ids: readonly string[]) => void;
}) {
  const count = countText(entry);
  return (
    <button
      type="button"
      aria-pressed={expanded === undefined ? selected : undefined}
      aria-expanded={expanded}
      onClick={onSelect}
      onMouseEnter={() => onHighlight?.(entry.highlight)}
      onMouseLeave={() => onHighlight?.([])}
      onFocus={() => onHighlight?.(entry.highlight)}
      onBlur={() => onHighlight?.([])}
      className={`grid w-full grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2 rounded-lg px-2 py-1 text-left transition ${selected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
    >
      <span
        aria-hidden="true"
        className={`text-center ${entry.preview.verb === 'rankUp' ? 'text-warn' : 'text-fg-subtle'}`}
      >
        {glyphOf(entry)}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className={`truncate ${entry.canDo ? '' : 'text-fg-muted'}`}>{entry.title}</span>
        {entry.upgrading.length > 0 ? (
          <span className="truncate text-xs text-accent">
            {entry.of > 1 ? `${entry.upgrading.length} upgrading` : 'Upgrading'}
            {entry.canDo ? ` · ${entry.headline}` : ' · stats apply when done'}
          </span>
        ) : (
          <span
            className={`truncate text-xs ${entry.canDo || entry.preview.enables.length > 0 ? 'text-fg-subtle' : 'text-warn'}`}
          >
            {entry.canDo ? entry.headline : (entry.preview.reason ?? entry.headline)}
          </span>
        )}
      </span>
      <span className="flex flex-col items-end text-xs leading-tight tabular-nums text-fg-muted">
        {count && <span className="text-fg-subtle">{count}</span>}
        <Cost cost={entry.preview.cost} stock={stock} />
      </span>
    </button>
  );
}

/**
 * A row that opens into the shared card (City panel; its action is pinned in
 * the panel's footer with `EntryAction`). The card keeps its Affects chips; the ring on the map follows the hover.
 */
export function UpgradeFold({
  entry,
  open,
  stock,
  onToggle,
  onHighlight,
}: {
  entry: UpgradeEntry;
  open: boolean;
  stock: Readonly<Record<string, number>>;
  onToggle: () => void;
  onHighlight?: (ids: readonly string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div>
        <UpgradeRow
          entry={entry}
          selected={open}
          expanded={open}
          stock={stock}
          onSelect={onToggle}
          onHighlight={onHighlight}
        />
      </div>
      {open && <UpgradeCard preview={entry.preview} externalAction onHighlight={onHighlight} />}
    </div>
  );
}

/** The action button row for the entry (a panel footer). */
export function EntryAction({
  entry,
  stock,
  dispatch,
}: {
  entry: UpgradeEntry;
  stock: Readonly<Record<string, number>>;
  dispatch: (c: Command) => void;
}) {
  return (
    <UpgradeActionRow
      preview={entry.preview}
      stock={stock}
      onConfirm={() => sendEntry(entry, dispatch)}
    />
  );
}
